import {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getTenantSlug } from '../../utils/subdomain';

/**
 * Fetches a white-label coach's branding from the current subdomain
 * (trainercompany.getnubod.com) BEFORE anyone logs in, and makes it
 * available to the whole tree:
 *   - ThemeProvider uses the colors as the pre-login palette.
 *   - The login / signup pages show the coach's logo + name.
 *   - The resolved coach is stashed for signup attribution.
 *
 * Public endpoint, so this works with no auth token. On a non-tenant
 * host (app.getnubod.com, localhost) it no-ops and everything renders
 * the default Nubod brand.
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

// Stash key for the resolved tenant so the signup flow can attribute a
// new client to the coach whose subdomain they landed on.
export const TENANT_STORAGE_KEY = 'nubod.tenant';

const EMPTY = {
  slug: null,
  brand: null,
  loading: false,
  found: false,
};

const SubdomainBrandContext = createContext(EMPTY);

export function SubdomainBrandProvider({ children }) {
  const slug = getTenantSlug();
  const [state, setState] = useState({ ...EMPTY, slug, loading: !!slug });

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/branding/by-subdomain/${encodeURIComponent(slug)}`
        );
        const data = await res.json();
        if (cancelled) return;
        const found = !!data?.found;
        setState({
          slug,
          brand: found ? data.brand : null,
          loading: false,
          found,
        });
        // Persist the tenant slug as a hint for a future signup flow.
        // Attribution itself must re-resolve the coach SERVER-SIDE from
        // the subdomain — this client-writable value is never trusted as
        // the source of truth for who a new client is assigned to.
        try {
          if (found) {
            localStorage.setItem(TENANT_STORAGE_KEY, JSON.stringify({ slug }));
          }
        } catch (_) {
          // localStorage may be blocked; non-fatal — branding still shows.
        }
      } catch (err) {
        // Network/again-later: fall back to default branding rather than
        // blocking the login page.
        if (!cancelled) setState({ ...EMPTY, slug, loading: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <SubdomainBrandContext.Provider value={state}>
      {children}
    </SubdomainBrandContext.Provider>
  );
}

export function useSubdomainBrand() {
  return useContext(SubdomainBrandContext);
}
